import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'reactstrap';
import { X, Check, Lock, Building2, Mail, User, Info, Eye, EyeOff, MessageSquare, ShieldCheck } from 'lucide-react';
import { guardarPerfilPropio, cambiarClavePropia } from '../services/userService';
import './mi-cuenta31.scss';

const WHATSAPP = '51942128139';
const MIN_CLAVE = 8;

// La sesión guardada en el navegador conserva `name` y `empresa`, y la
// revalidación de App.js no los refresca: sin este parche el nombre viejo
// reaparece al recargar la página, aunque la base ya tenga el nuevo.
const almacenSesion = () => (sessionStorage.getItem('nuvia_user') ? sessionStorage : localStorage);
const parcharSesion = (parches) => {
  try {
    const almacen = almacenSesion();
    const guardado = almacen.getItem('nuvia_user');
    if (!guardado) return;
    almacen.setItem('nuvia_user', JSON.stringify({ ...JSON.parse(guardado), ...parches }));
  } catch {
    // Sesión ilegible o almacenamiento bloqueado: en la base ya quedó bien.
  }
};

const Aviso = ({ estado }) => {
  if (!estado) return null;
  const esError = estado.tipo === 'error';
  return (
    <p className={'mc31-aviso ' + (esError ? 'mc31-aviso-error' : 'mc31-aviso-ok')} role="status">
      {esError ? <Info size={16} /> : <Check size={16} />}
      <span>{estado.texto}</span>
    </p>
  );
};

const CampoClave = ({ id, label, valor, onChange, autoComplete, hint }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="mc31-campo">
      <label htmlFor={id}>{label}</label>
      <div className="mc31-clave-input">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={valor}
          autoComplete={autoComplete}
          onChange={event => onChange(event.target.value)}
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        >
          {visible ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </div>
      {hint && <small>{hint}</small>}
    </div>
  );
};

/**
 * Mi cuenta — el usuario edita su nombre, su empresa y su contraseña.
 *
 * El correo se muestra pero no se edita: es la llave con la que la base guarda
 * su seguimiento, su calendario, sus procesos revisados y los datos de «Mi
 * Empresa». Cambiarlo obliga a mover también esas filas, así que lo hace el
 * administrador; aquí solo se le pide por WhatsApp.
 */
export default function MiCuenta31({ isOpen, toggle, user, perfil, onPerfilGuardado }) {
  const correo = perfil?.correo || user?.email || '';
  const nombreGuardado = perfil?.nombre || '';
  const empresaGuardada = perfil?.empresa || '';

  const [nombre, setNombre] = useState(nombreGuardado);
  const [empresa, setEmpresa] = useState(empresaGuardada);
  const [guardandoDatos, setGuardandoDatos] = useState(false);
  const [avisoDatos, setAvisoDatos] = useState(null);

  const [claveActual, setClaveActual] = useState('');
  const [claveNueva, setClaveNueva] = useState('');
  const [claveRepetida, setClaveRepetida] = useState('');
  const [guardandoClave, setGuardandoClave] = useState(false);
  const [avisoClave, setAvisoClave] = useState(null);

  // Cada apertura parte de lo que hay en la base: si el usuario cerró el modal
  // a medio escribir, no se queda con el borrador de la vez anterior.
  useEffect(() => {
    if (!isOpen) return;
    setNombre(nombreGuardado);
    setEmpresa(empresaGuardada);
    setAvisoDatos(null);
    setClaveActual('');
    setClaveNueva('');
    setClaveRepetida('');
    setAvisoClave(null);
  }, [isOpen, nombreGuardado, empresaGuardada]);

  const datosTocados = nombre.trim() !== nombreGuardado.trim() || empresa.trim() !== empresaGuardada.trim();
  const datosCompletos = nombre.trim() !== '' && empresa.trim() !== '';

  const errorClave = useMemo(() => {
    if (claveActual === '' && claveNueva === '' && claveRepetida === '') return null;
    if (claveNueva !== '' && claveNueva.length < MIN_CLAVE) return 'La contraseña nueva necesita al menos ' + MIN_CLAVE + ' caracteres.';
    if (claveRepetida !== '' && claveNueva !== claveRepetida) return 'Las dos contraseñas nuevas no coinciden.';
    if (claveNueva !== '' && claveNueva === claveActual) return 'La contraseña nueva tiene que ser distinta de la actual.';
    return null;
  }, [claveActual, claveNueva, claveRepetida]);

  const claveLista = claveActual !== '' && claveNueva.length >= MIN_CLAVE && claveNueva === claveRepetida && !errorClave;

  const guardarDatos = async (event) => {
    event.preventDefault();
    if (!datosTocados || !datosCompletos || guardandoDatos) return;

    setGuardandoDatos(true);
    setAvisoDatos(null);
    const resultado = await guardarPerfilPropio(correo, { nombre: nombre.trim(), empresa: empresa.trim() });
    setGuardandoDatos(false);

    if (!resultado.ok) {
      setAvisoDatos({ tipo: 'error', texto: resultado.mensaje });
      return;
    }

    parcharSesion({ name: resultado.perfil.nombre, empresa: resultado.perfil.empresa });
    setAvisoDatos({ tipo: 'ok', texto: 'Tus datos quedaron guardados.' });
    onPerfilGuardado?.();
  };

  const guardarClave = async (event) => {
    event.preventDefault();
    if (!claveLista || guardandoClave) return;

    setGuardandoClave(true);
    setAvisoClave(null);
    const resultado = await cambiarClavePropia(correo, claveActual, claveNueva);
    setGuardandoClave(false);

    if (!resultado.ok) {
      setAvisoClave({ tipo: 'error', texto: resultado.mensaje });
      return;
    }

    setClaveActual('');
    setClaveNueva('');
    setClaveRepetida('');
    setAvisoClave({ tipo: 'ok', texto: 'Contraseña actualizada. Úsala la próxima vez que entres.' });
  };

  // window.open tiene que salir del propio clic: después de un await el
  // navegador lo bloquearía como ventana emergente.
  const pedirCambioDeCorreo = () => {
    const mensaje = 'Hola, quiero cambiar el correo de mi cuenta de LicitaHUB.'
      + ' Mi correo actual es ' + correo + ' y quiero usar: ';
    window.open('https://wa.me/' + WHATSAPP + '?text=' + encodeURIComponent(mensaje), '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal zIndex={12000} isOpen={isOpen} toggle={toggle} centered className="mc31" labelledBy="mc31-title">
      <div className="mc31-head">
        <div>
          <h2 id="mc31-title">Mi cuenta</h2>
          <p>Tus datos y tu contraseña de acceso</p>
        </div>
        <button type="button" className="mc31-close" onClick={toggle} aria-label="Cerrar">
          <X size={18} />
        </button>
      </div>

      <div className="mc31-body">
        <form className="mc31-bloque" onSubmit={guardarDatos}>
          <h3><User size={17} />Mis datos</h3>

          <div className="mc31-fila">
            <div className="mc31-campo">
              <label htmlFor="mc31-nombre">Nombre</label>
              <input
                id="mc31-nombre"
                type="text"
                maxLength={120}
                value={nombre}
                autoComplete="name"
                placeholder="Nombre y apellido"
                onChange={event => setNombre(event.target.value)}
              />
            </div>
            <div className="mc31-campo">
              <label htmlFor="mc31-empresa"><Building2 size={14} />Empresa</label>
              <input
                id="mc31-empresa"
                type="text"
                maxLength={160}
                value={empresa}
                autoComplete="organization"
                placeholder="Razón social"
                onChange={event => setEmpresa(event.target.value)}
              />
            </div>
          </div>

          <div className="mc31-campo mc31-correo">
            <label htmlFor="mc31-correo"><Mail size={14} />Correo</label>
            <div className="mc31-correo-input">
              <input id="mc31-correo" type="email" value={correo} readOnly disabled />
              <Lock size={15} />
            </div>
            <small>
              Con este correo guardamos tu seguimiento, tu calendario, tus procesos revisados y
              los datos de Mi Empresa. Lo cambiamos nosotros para que no pierdas nada.
            </small>
            <button type="button" className="mc31-enlace" onClick={pedirCambioDeCorreo}>
              <MessageSquare size={15} />Solicitar cambio de correo
            </button>
          </div>

          <div className="mc31-pie">
            <Aviso estado={avisoDatos} />
            <button type="submit" className="mc31-boton mc31-primario" disabled={!datosTocados || !datosCompletos || guardandoDatos}>
              {guardandoDatos ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </form>

        <form className="mc31-bloque" onSubmit={guardarClave}>
          <h3><ShieldCheck size={17} />Contraseña</h3>

          <CampoClave
            id="mc31-clave-actual"
            label="Contraseña actual"
            valor={claveActual}
            onChange={setClaveActual}
            autoComplete="current-password"
            hint="Si nunca la cambiaste, es el RUC con el que entras."
          />
          <div className="mc31-fila">
            <CampoClave
              id="mc31-clave-nueva"
              label="Contraseña nueva"
              valor={claveNueva}
              onChange={setClaveNueva}
              autoComplete="new-password"
              hint={'Mínimo ' + MIN_CLAVE + ' caracteres.'}
            />
            <CampoClave
              id="mc31-clave-repetida"
              label="Repetir la nueva"
              valor={claveRepetida}
              onChange={setClaveRepetida}
              autoComplete="new-password"
            />
          </div>

          <div className="mc31-pie">
            <Aviso estado={errorClave ? { tipo: 'error', texto: errorClave } : avisoClave} />
            <button type="submit" className="mc31-boton mc31-primario" disabled={!claveLista || guardandoClave}>
              {guardandoClave ? 'Cambiando…' : 'Cambiar contraseña'}
            </button>
          </div>
        </form>

        <p className="mc31-nota">
          <Info size={15} />
          <span>Tu plan, tu vigencia y tu RUC los administra el equipo de LicitaHUB. Escríbenos si hay algo que corregir.</span>
        </p>
      </div>
    </Modal>
  );
}
